import { always, cond, contains, filter, groupBy, isNil, pipe, prop, T } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Heading, NotificationContainer, Search, Text, TextButton } from '../../components/common';
import { EditImagesAndVideoSection } from '../../components/common/MediaHome/EditImagesAndVideoSection';
import { CreateNewMedia } from '../../components/common/MediaHome/EditImagesAndVideoSection/CreateNewMedia';
import { MediaHomeEditService } from '../../components/common/MediaHome/EditImagesAndVideoSection/EditImagesAndVideoSection.types';
import { OpenSource } from '../../constants/Bi';
import { MainSource } from '../../constants/Source';
import fileManagerStyles from '../FilesManager/FilesManager.scss';
import { stopPropagation } from '../../utils/dom/domEvents';
import { JumpTo } from '../../utils/hoc/withItemsReducer/withItemsReducer.types';
import { useMMGRSettings } from '../../utils/hoc/withMMGRSettings';
import { HomeButtonName, useTrackMediaHomeAnalytics } from '../../utils/hooks/bi/useTrackMediaHomeAnalytics';
import { useTrackOpenEditingTools } from '../../utils/hooks/bi/useTrackOpenEditingTools';
import { useLoadingErrorCatcher } from '../../utils/hooks/useLoadingErrorCatcher';
import { useMediaStudio, useMediaStudioOnFileUploaded } from '../../utils/hooks/useMediaStudio';
import { useVideoMaker } from '../../utils/hooks/useVideoMaker';
import { useVideoMakerOnFileUploaded } from '../../utils/hooks/useVideoMaker/useVideoMaker';
import { MediaManagerSDKAnyItem } from '../../utils/media-manager-sdk';
import { isSiteFiles } from '../../utils/media-manager-sdk/sources';
import { ExpandedView } from '../ExpandedView';
import { ContextMenus } from '../FilesManager/ContextMenus';
import { EmptyState } from '../FilesManager/EmptyState';
import { DiscoverMediaTypesSection } from './DiscoverMediaTypesSection';
import { discoverMediaTypesSettings } from './discoverMediaTypesSettings';
import styles from './Home.scss';
import { IInner, IOuter } from './Home.types';
import { LoadingSection } from './LoadingSection';
import { HorizontalSection } from '../../components/common/HorizontalSection';
import { RecentFilesSection } from './RecentFilesSection';
import { RecommendedSection } from './RecommendedSection';
import { useRecommendedCategoriesModal } from '../Root/hooks/useRecommendedCategoriesModal';

export const HomeComponent: FC<IOuter & IInner> = ({
  buyItem,
  selectedItems,
  isSecure,
  beginDrag,
  publicMediaCurrentCategory,
  selectRecommendedCategories,
  recommendedCategories,
  recommendedCategoriesError,
  isRecommendedCategoriesLoading,
  isLoadingRecommendedItems,
  handleItemClick,
  handleFileDoubleClick,
  isItemSelected,
  openExpandedView,
  isInExpandedMode,
  editingItem,
  handleJumpToNextItem,
  closeExpandedView,
  handleEditExpandedItemCancel,
  handleNameChange,
  handleEditItemComplete,
  footerContent,
  items,
  demonstrationThumbnailStyle,
  isLoadingRecentItems,
  handleItemRightClick,
  handleSearchEnterPressed,
  handleSearchClearButtonClick,
  searchBiParams,
  clearItemsSelection,
  searchQuery,
  openFolderById,
  selectItem,
  onUndoRemove,
  refreshFolder,
  settingsLayout,
  prependItems,
  currentFolder,
}) => {
  const { formatMessage } = useIntl();
  const { mmgrSettings } = useMMGRSettings();
  const { isUnknownLoadingError, isNotAuthorized, loadingError } = useLoadingErrorCatcher();

  const { openSource, metaSiteToken, hideCreateAndEditActions } = mmgrSettings;

  const { recentItems = [], recommendedItems = [] } = useMemo(
    () =>
      groupBy<MediaManagerSDKAnyItem>(
        pipe(
          prop('source'),
          cond([
            [isNil, always('recommendedADIItems')],
            [isSiteFiles, always('recentItems')],
            [T, always('recommendedItems')],
          ]),
        ),
        items,
      ),
    [items],
  );

  const shouldShowVideoMakerSection = Boolean(metaSiteToken) && openSource !== OpenSource.VideoMaker;

  const { openMediaStudio } = useMediaStudio();

  const { openVideoMaker } = useVideoMaker();
  const { trackVideoMakerOpen, trackMediaStudioOpen } = useTrackOpenEditingTools();

  const recommendedCategoriesModal = useRecommendedCategoriesModal(recommendedCategories);

  const handleVideoMakerOpen = useCallback(() => {
    openVideoMaker();
    trackVideoMakerOpen();
  }, [openVideoMaker, trackVideoMakerOpen]);

  const handleMediaStudioOpen = useCallback(() => {
    openMediaStudio();
    trackMediaStudioOpen();
  }, [openMediaStudio, trackMediaStudioOpen]);

  const handleFileUpload = useCallback(
    (file) => {
      prependItems([file]);
      selectItem(file.id);
    },
    [prependItems, selectItem],
  );

  const hasRecentItems = recentItems.length > 0;

  const { trackRecentButtonClick } = useTrackMediaHomeAnalytics();

  const history = useHistory();

  const handleSeeAllClick = useCallback(
    (event) => {
      stopPropagation(event);
      history.push(MainSource.SiteFiles);
      trackRecentButtonClick(HomeButtonName.SeeAll);
    },
    [trackRecentButtonClick],
  );

  const {
    mmgrSettings: { publicMediaRoot, tabsList },
  } = useMMGRSettings();
  const mediaTypesSettings = filter((setting) => contains(setting.root, publicMediaRoot), discoverMediaTypesSettings);
  const countItems = mediaTypesSettings.length;

  const isMediaFromWixAvailable = contains(MainSource.MediaFromWix, tabsList);
  const shouldDisplayMediaTypesSection = isMediaFromWixAvailable && countItems > 1;

  useMediaStudioOnFileUploaded(handleFileUpload);
  useVideoMakerOnFileUploaded(handleFileUpload);

  const messageProps = useMemo(() => ({ onUndoRemove, openFolderById }), [onUndoRemove, openFolderById]);

  if (isNotAuthorized || isUnknownLoadingError) {
    return (
      <EmptyState
        isUnknownError={isUnknownLoadingError}
        source={MainSource.Home}
        settingsLayout={settingsLayout}
        refreshFolder={refreshFolder}
        dataHook="files-manager-empty-state"
      />
    );
  }

  return (
    <>
      <NotificationContainer className={fileManagerStyles.notificationsContainer} messageProps={messageProps} />

      <div className={styles.searchWrapper}>
        <div className={styles.fullWidthSearch}>
          <Search
            value={searchQuery}
            onEnterPressed={handleSearchEnterPressed}
            onClearButtonClick={handleSearchClearButtonClick}
            biParams={searchBiParams}
            isAlwaysActive
            placeholder={formatMessage({ id: 'search.input-placeholder-new-layout' })}
          />
        </div>
      </div>

      <div className={styles.homeHolder} onMouseDown={clearItemsSelection} data-hook="home">
        <div className={styles.homeWrapper}>
          <div className={styles.homeContent}>
            <Heading size="medium" className={styles.title}>
              <FormattedMessage id="media-home.title" />
            </Heading>

            {isLoadingRecentItems ? (
              <HorizontalSection>
                <LoadingSection dataHook="recent-files-loader" />
              </HorizontalSection>
            ) : (
              <>
                {hasRecentItems && (
                  <HorizontalSection
                    header={
                      <div className={styles.recentFilesHeader}>
                        <div>
                          <Text weight="bold" size="small">
                            <FormattedMessage id="media-home.recent-files-section.header" />
                          </Text>
                        </div>

                        <div className={styles.recentFilesSeeAll}>
                          <TextButton size="small" onClick={handleSeeAllClick} dataHook="recent-see-all-button">
                            <FormattedMessage id="media-home.recent-files-section.see-all" />
                          </TextButton>
                        </div>
                      </div>
                    }
                  >
                    <RecentFilesSection
                      dataHook="recent-files-section"
                      items={recentItems}
                      openExpandedView={openExpandedView}
                      buyItem={buyItem}
                      onItemClick={handleItemClick}
                      onItemDoubleClick={handleFileDoubleClick}
                      isItemSelected={isItemSelected}
                      selectedItems={selectedItems}
                      isSecure={isSecure}
                      beginDrag={beginDrag}
                      publicMediaCurrentCategory={publicMediaCurrentCategory}
                      onItemRightClick={handleItemRightClick}
                      currentFolder={currentFolder}
                    />
                  </HorizontalSection>
                )}

                <RecommendedSection
                  dataHook="recommended-section"
                  isLoading={isLoadingRecommendedItems}
                  items={recommendedItems}
                  openExpandedView={openExpandedView}
                  buyItem={buyItem}
                  onItemClick={handleItemClick}
                  onItemDoubleClick={handleFileDoubleClick}
                  isItemSelected={isItemSelected}
                  selectedItems={selectedItems}
                  isSecure={isSecure}
                  beginDrag={beginDrag}
                  publicMediaCurrentCategory={publicMediaCurrentCategory}
                  selectRecommendedCategories={selectRecommendedCategories}
                  recommendedCategories={recommendedCategories}
                  recommendedCategoriesError={recommendedCategoriesError}
                  isRecommendedCategoriesLoading={isRecommendedCategoriesLoading}
                  onItemRightClick={handleItemRightClick}
                  loadingError={loadingError}
                  currentFolder={currentFolder}
                />
              </>
            )}

            {!hideCreateAndEditActions && (
              <HorizontalSection
                header={
                  <Text weight="bold" size="small">
                    <FormattedMessage id="media-home.edit-images-and-videos.label" />
                  </Text>
                }
              >
                <EditImagesAndVideoSection dataHook="edit-images-videos-section">
                  <CreateNewMedia editServiceName={MediaHomeEditService.PhotoStudio} onClick={handleMediaStudioOpen} />

                  {shouldShowVideoMakerSection && (
                    <CreateNewMedia editServiceName={MediaHomeEditService.VideoMaker} onClick={handleVideoMakerOpen} />
                  )}
                </EditImagesAndVideoSection>
              </HorizontalSection>
            )}
            {shouldDisplayMediaTypesSection && (
              <HorizontalSection
                header={
                  <Text weight="bold" size="small">
                    <FormattedMessage id="media-home.discover-media-types.label" />
                  </Text>
                }
              >
                <DiscoverMediaTypesSection />
              </HorizontalSection>
            )}
          </div>
        </div>
      </div>

      <div className={fileManagerStyles.footer}>{footerContent}</div>
      {recommendedCategoriesModal}

      <TransitionGroup>
        {isInExpandedMode && (
          <CSSTransition classNames={fileManagerStyles} timeout={300}>
            <ExpandedView
              className={fileManagerStyles.expandedView}
              items={selectedItems}
              editingItem={editingItem}
              jumpToNextItem={handleJumpToNextItem({ jumpTo: JumpTo.Next })}
              jumpToPrevItem={handleJumpToNextItem({ jumpTo: JumpTo.Prev })}
              onClose={closeExpandedView}
              onRenameCancel={handleEditExpandedItemCancel}
              onNameChange={handleNameChange}
              onRenameComplete={handleEditItemComplete}
              footerContent={footerContent}
              totalItemsCount={items.length}
              demonstrationThumbnailStyle={demonstrationThumbnailStyle}
            />
          </CSSTransition>
        )}
      </TransitionGroup>

      <ContextMenus
        selectedItems={selectedItems}
        source={MainSource.Home}
        currentFolder={currentFolder}
        openExpandedView={openExpandedView}
        isInExpandedMode={isInExpandedMode}
        layout={null}
        layouts={[]}
        setLayout={noop}
        setSort={noop}
        createNewFolderStart={noop}
        hasCutItems={false}
        hasCopiedItems={false}
        copySelectedItems={noop}
        cutSelectedItems={noop}
        pasteItems={noop}
        removeSelectedItemsFromBoard={noop}
        handleEditItemStart={noop}
        copyFileUrlToClipboard={noop}
        copyWixUrlToClipboard={noop}
        restoreItems={noop}
        moveItemsToTrash={noop}
        startDownloadItem={noop}
        onDownloadSelectedItems={noop}
      />
    </>
  );
};
